/* The .timer color shift uses a 0.4s literal (slow enough that the
 * tone escalation paces across threshold boundaries instead of
 * snapping). Not tokenized because it's a one-off duration — the
 * overlay only ships --motion-duration-fast for the 30+ snappy
 * hover/focus transitions. Revisit if a `--motion-duration-medium`
 * tier lands. */

/* Top padding is large (80px) because this is a first-run hero screen
 * — Figma 1148:562 positions the brand mark 80px from the frame edge,
 * giving the mark visual room to breathe. Tokens cap at 28px so the
 * literal stands; swap to a semantic `--hero-top` token when design
 * ships one. */
.container {
  display: flex;
  flex-direction: column;
  padding: 80px var(--spacing-16) var(--spacing-16);
  min-height: 100%;
  box-sizing: border-box;
}

/* First-run brand moment — the Logo sits at the top of every pairing
 * state, since AppShell intentionally omits HeaderBar chrome until the
 * user is paired. The Logo primitive itself ships with zero internal
 * padding, so all of the pairing-screen breathing room lives on this
 * wrapper. */
.brandMark {
  display: flex;
  justify-content: center;
  /* 80px bottom gap mirrors the Figma 1148:562 spacing (82px between
   * the logo's bottom and the "Enter this code…" title). Tokens cap
   * at 28px so the literal stands; swap to a semantic `--hero-gap`
   * token when design ships one. */
  margin-bottom: 80px;
}

/* Single-line heading. No separate subtitle — the 6-box grid below
 * makes the "this is a pairing code" context implicit, so one focused
 * directive replaces the former two-line header+subtitle pair. */
.title {
  margin: 0 0 var(--spacing-16);
  font: var(--text-subtitle-semibold);
  color: var(--text-primary);
  text-align: center;
}

/* The main body region below the header — either the 6-box code
 * grid + status line, or a checkmark success block. Centered column
 * so every state aligns identically under the header. */
.codeArea {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-12);
}

.centered {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-12);
}

/* Visually-hidden utility. Keeps the element in the accessibility
 * tree (screen readers can read it, live regions announce changes to
 * it) while making it invisible to sighted users. Standard sr-only
 * pattern — WCAG-friendly, doesn't affect layout. Used by the
 * codeGrid's live-region content span so AT users hear state
 * transitions and the actual 6-digit pairing code. */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* ─── Code grid ───────────────────────────────────────────────── */

/* 6 boxes per Figma 1148:562 — each is 44×48px, 4px radius, filled
 * with --button-secondary-default. When the code isn't present
 * (loading/expired/error) the box renders empty; when showCode is
 * active, each cell fills with one digit. Layout stays stable across
 * state transitions so users don't see jarring reflows. */
.codeGrid {
  display: flex;
  gap: var(--spacing-8);
  align-items: center;
  justify-content: center;
}

.codeBox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 48px;
  padding: var(--spacing-4);
  background: var(--button-secondary-default);
  border-radius: var(--radius-4);
  box-sizing: border-box;
}

/* Digit glyph: Figma specs 20px/28px semibold with -0.2px tracking.
 * No composite "heading-l" token today — compose from atomic tokens. */
.codeDigit {
  font-family: var(--font-family);
  font-weight: var(--text-weight-semibold);
  font-size: var(--text-size-xl);
  line-height: var(--text-lh-xl);
  letter-spacing: -0.2px;
  color: var(--text-primary);
  /* Staggered entrance — each digit rises into place 40ms apart, so
   * the code appears as a small choreography rather than a silent
   * swap. `both` preserves the from-state before the animation
   * starts AND the to-state after it finishes. Indices are set via
   * `:nth-child` on the parent .codeBox so animation-delay scales
   * naturally with the grid position. */
  animation: digitRiseIn 220ms cubic-bezier(0.2, 0.8, 0.3, 1) both;
}

/* Selectors target nth-child positions 2–7 because the codeGrid's
 * first child is the `.srOnly` live-region text span (see index.tsx).
 * The six .codeBox elements sit at positions 2 through 7; the
 * animation-delay progression (0ms → 200ms in 40ms steps) stays the
 * same, just shifted by one selector slot. */
.codeBox:nth-child(2) .codeDigit {
  animation-delay: 0ms;
}
.codeBox:nth-child(3) .codeDigit {
  animation-delay: 40ms;
}
.codeBox:nth-child(4) .codeDigit {
  animation-delay: 80ms;
}
.codeBox:nth-child(5) .codeDigit {
  animation-delay: 120ms;
}
.codeBox:nth-child(6) .codeDigit {
  animation-delay: 160ms;
}
.codeBox:nth-child(7) .codeDigit {
  animation-delay: 200ms;
}

@keyframes digitRiseIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Users opting out of motion see the digits appear in place — same
 * final state, no animation. */
@media (prefers-reduced-motion: reduce) {
  .codeDigit {
    animation: none;
  }
}

/* ─── Status lines below the grid ────────────────────────────── */

.status {
  margin: 0;
  font: var(--text-micro-regular);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-secondary);
}

/* Timer tone escalates as the code's life runs out. Tertiary neutral
 * is the default; ≤60s promotes to secondary (slightly more prominent
 * against the background); ≤30s shifts to the negative-tone semantic
 * color so expiry urgency reads without extra copy. `transition`
 * smooths the threshold jump so the color shift feels paced rather
 * than abrupt. */
.timer {
  margin: 0;
  font-size: var(--text-size-s);
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
  transition: color 0.4s ease;
}

.timerWarning {
  color: var(--text-secondary);
}

.timerUrgent {
  color: var(--semantic-negative);
}

.errorText {
  margin: 0;
  font-size: var(--text-size-s);
  color: var(--semantic-negative);
  text-align: center;
}

/* ─── Retry row (expired / error) ────────────────────────────── */

/* The parent .codeArea is a centered flex column, so a naked child
 * hugs its content instead of stretching. This wrapper provides the
 * stretched layout hole the Button primitive needs for fullWidth to
 * actually render full-width. Margin-top matches the grid→status
 * rhythm in the rest of the state tree (spacing-4 above the row). */
.retryRow {
  margin-top: var(--spacing-4);
  align-self: stretch;
}

/* ─── Confirmed state (success checkmark) ────────────────────── */

.checkmark {
  color: var(--semantic-success);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success {
  margin: 0;
  font: var(--text-subtitle-medium);
  color: var(--semantic-success);
}

/* ─── Footer help hint ───────────────────────────────────────── */

/* Persistent across every state so a user who lands on this screen
 * without the Reflecto mobile app can always find the path to get
 * it. `margin-top: auto` pins it to the bottom of the container
 * regardless of how much content sits above. */
.helpHint {
  margin: auto 0 0;
  padding-top: var(--spacing-24);
  font-size: var(--text-size-xs);
  color: var(--text-tertiary);
  text-align: center;
}

.helpHint a {
  /* Emerald brand — PR #273 established `--brand` (not `--text-link`)
   * as the correct token for this pairing-hint link so the CTA tone
   * matches the other brand touchpoints on the screen. */
  color: var(--brand);
  text-decoration: underline;
}
